import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRESETS = [
  { key: '1m', label: '1 month' },
  { key: '3m', label: '3 months' },
  { key: '6m', label: '6 months' },
  { key: '9m', label: '9 months' },
  { key: '1y', label: '1 year' },
];

const DEFAULT_CONFIG = {
  offset_anchor: 'date_of_service',
  output_types: ['notification'],
  draft_config: { target_template_id: '', target_template_name: '' },
  recipients: { include_rendering_provider: true, additional_user_ids: [] },
  allowed_offsets: ['1m', '3m', '6m', '9m', '1y'],
  allow_custom: true,
};

export const ReminderFieldConfig = ({ field, updateFormField }) => {
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const config = { ...DEFAULT_CONFIG, ...(field.config || {}) };

  useEffect(() => {
    axios.get(`${API}/form-templates`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.templates || [];
      setTemplates(list.filter((t) => t.is_active !== false));
    }).catch(() => {});
    axios.get(`${API}/users`).then((r) => {
      const list = Array.isArray(r.data) ? r.data : r.data?.users || [];
      setUsers(list.filter((u) => u.is_active !== false && !u.is_archived));
    }).catch(() => {});
  }, []);

  const setConfig = (patch) => updateFormField(field.id, { config: { ...config, ...patch } });

  const toggleOutput = (t) => {
    const cur = config.output_types || [];
    setConfig({ output_types: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] });
  };

  const toggleOffset = (k) => {
    const cur = config.allowed_offsets || [];
    setConfig({ allowed_offsets: cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k] });
  };

  const toggleUser = (uid) => {
    const cur = config.recipients?.additional_user_ids || [];
    setConfig({
      recipients: {
        ...config.recipients,
        additional_user_ids: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid],
      },
    });
  };

  return (
    <div className="space-y-3 border rounded-md p-3 bg-amber-50/50 border-amber-200" data-testid={`reminder-config-${field.id}`}>
      <p className="text-xs font-semibold text-amber-800">Reminder Settings</p>

      <div>
        <p className="text-xs font-medium mb-1">Instructions / helper text (optional):</p>
        <input
          type="text"
          className="w-full text-xs border rounded px-2 py-1.5 bg-white"
          placeholder="e.g. Trigger this reminder 2 weeks before the review is due"
          maxLength={300}
          value={config.helper_text || ''}
          onChange={(e) => setConfig({ helper_text: e.target.value })}
          data-testid={`reminder-helper-text-${field.id}`}
        />
        <p className="text-[10px] text-gray-500 mt-0.5">Shown to the signer under the field and included with the reminder.</p>
      </div>

      <div>
        <p className="text-xs font-medium mb-1">Creates (pick at least one):</p>
        <div className="flex flex-wrap gap-3">
          {['notification', 'task', 'draft'].map((t) => (
            <label key={t} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5" checked={(config.output_types || []).includes(t)}
                onChange={() => toggleOutput(t)} data-testid={`reminder-output-${t}-${field.id}`} />
              {t === 'draft' ? 'Draft Document' : t.charAt(0).toUpperCase() + t.slice(1)}
            </label>
          ))}
        </div>
      </div>

      {(config.output_types || []).includes('draft') && (
        <div>
          <p className="text-xs font-medium mb-1">Draft document template:</p>
          <select
            className="w-full text-xs border rounded px-2 py-1.5 bg-white"
            value={config.draft_config?.target_template_id || ''}
            onChange={(e) => {
              const t = templates.find((x) => x.id === e.target.value);
              setConfig({ draft_config: { target_template_id: e.target.value, target_template_name: t?.name || '' } });
            }}
            data-testid={`reminder-draft-template-${field.id}`}
          >
            <option value="">Select a template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      <div>
        <p className="text-xs font-medium mb-1">Offset anchor:</p>
        <select
          className="w-full text-xs border rounded px-2 py-1.5 bg-white"
          value={config.offset_anchor}
          onChange={(e) => setConfig({ offset_anchor: e.target.value })}
          data-testid={`reminder-anchor-${field.id}`}
        >
          <option value="date_of_service">Date of Service</option>
          <option value="signed_date">Signed Date</option>
        </select>
      </div>

      <div>
        <p className="text-xs font-medium mb-1">Preset offsets shown to the signer:</p>
        <div className="flex flex-wrap gap-3">
          {PRESETS.map((p) => (
            <label key={p.key} className="flex items-center gap-1.5 text-xs cursor-pointer">
              <input type="checkbox" className="h-3.5 w-3.5" checked={(config.allowed_offsets || []).includes(p.key)}
                onChange={() => toggleOffset(p.key)} />
              {p.label}
            </label>
          ))}
          <label className="flex items-center gap-1.5 text-xs cursor-pointer text-gray-600">
            <input type="checkbox" className="h-3.5 w-3.5" checked={config.allow_custom !== false}
              onChange={(e) => setConfig({ allow_custom: e.target.checked })} />
            Allow custom date
          </label>
        </div>
      </div>

      <div>
        <p className="text-xs font-medium mb-1">Recipients:</p>
        <label className="flex items-center gap-1.5 text-xs cursor-pointer mb-1.5">
          <input type="checkbox" className="h-3.5 w-3.5" checked={config.recipients?.include_rendering_provider !== false}
            onChange={(e) => setConfig({ recipients: { ...config.recipients, include_rendering_provider: e.target.checked } })} />
          Provider on the note (default)
        </label>
        <select
          className="w-full text-xs border rounded px-2 py-1.5 bg-white"
          value=""
          onChange={(e) => e.target.value && toggleUser(e.target.value)}
          data-testid={`reminder-add-user-${field.id}`}
        >
          <option value="">+ Add additional recipient…</option>
          {users.filter((u) => !(config.recipients?.additional_user_ids || []).includes(u.id))
            .map((u) => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
        </select>
        {(config.recipients?.additional_user_ids || []).length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {(config.recipients.additional_user_ids || []).map((uid) => {
              const u = users.find((x) => x.id === uid);
              return (
                <span key={uid} className="inline-flex items-center gap-1 text-[11px] bg-white border rounded-full px-2 py-0.5">
                  {u?.full_name || u?.email || uid}
                  <button className="text-gray-400 hover:text-red-600" onClick={() => toggleUser(uid)}>×</button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReminderFieldConfig;
